// History drawer — read-only audit log for a single entity (feature,
// release, item, or prompt).
//
// Backed by GET /api/history?entity_type&entity_id (editor-only). Each row
// is one PATCH / publish / unpublish; the `changes` payload is field-level
// before → after snapshots. Renders newest-first, grouped by day.
//
// This is Phase 1 of the E3 (version history) work in hub-critique.md.
// Diff/compare + revert live in Phase 2 — the schema + capture already
// support them; only the UI is missing.
import { useEffect, useMemo, useState } from "react";
import type { EditHistoryChange, EditHistoryEntry } from "@release-hub/shared";
import { ApiError } from "@/api/client";
import { useRevertHistory } from "@/api/mutations";
import { useEditHistory } from "@/api/queries";
import { useTrack } from "@/lib/track";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { useConfirm } from "@/ui/confirm";
import { fmtDateTime } from "@/lib/format";

/** Human labels for the entity types we log — matches the labels shown
 *  on the corresponding editor pages. */
const ENTITY_LABEL: Record<string, string> = {
  feature_entries: "Feature",
  releases: "Release",
  release_items: "Release item",
  llm_prompts: "Prompt",
};

/** Human labels for known columns. Anything not listed falls through to
 *  the raw column name. */
const FIELD_LABEL: Record<string, string> = {
  name: "Name",
  pitch: "Pitch",
  category: "Category",
  beta: "Beta",
  flag_ids: "Flags",
  surface: "Surface",
  feature_family: "Family",
  parity_notes: "Parity",
  projected_ga: "Projected GA",
  projected_ga_confidence: "Projected GA confidence",
  known_gaps: "Known gaps",
  demo_url: "Demo",
  owner_email: "Owner",
  linear_ticket_ids: "Linear tickets",
  linear_project_id: "Linear project",
  prd_url: "PRD",
  github_pr_urls: "GitHub PRs",
  internal_only: "Internal only",
  announcement_state: "Announcement state",
  announcement_date: "Announcement date",
  announcement_narrative: "Announcement narrative",
  audience: "Audience",
  why_it_matters: "Why it matters",
  title: "Title",
  narrative: "Narrative",
  media_urls: "Media",
  release_date_override: "Release date override",
  source_body_override: "Source body override",
  text: "Text",
  rollout_override: "Rollout override",
  linked_flag_ids: "Linked flags",
  content: "Content",
  status: "Status",
};

const ACTION_LABEL: Record<string, string> = {
  update: "edited",
  publish: "published",
  unpublish: "unpublished",
  create: "created",
  delete: "deleted",
};

export function HistoryDrawer({
  open,
  onOpenChange,
  entityType,
  entityId,
  title,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  entityType: string;
  entityId: string | undefined;
  /** Optional prefix — e.g. the feature name — surfaced in the header. */
  title?: string;
}) {
  const enabled = open && !!entityId;
  const { data, isPending, error } = useEditHistory(
    enabled ? entityType : undefined,
    enabled ? entityId : undefined,
  );
  const entityLabel = ENTITY_LABEL[entityType] ?? entityType;
  const track = useTrack();

  // One track per open — not per re-render.
  useEffect(() => {
    if (open && entityId) {
      track({
        event: "history_open",
        entity_type: entityType,
        entity_id: entityId,
      });
    }
    // Only re-fire when the drawer opens for a new entity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityType, entityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit history</DialogTitle>
          <DialogDescription>
            {entityLabel}
            {title ? ` · ${title}` : ""} — newest first. Field-level diffs.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          {isPending && <p className="text-text-secondary">Loading…</p>}
          {error && (
            <p className="text-destructive">
              Failed to load:{" "}
              {error instanceof Error ? error.message : "unknown"}
            </p>
          )}
          {data && data.length === 0 && (
            <p className="text-text-tertiary">No edits recorded yet.</p>
          )}
          {data?.map((entry) => (
            <HistoryEntry key={entry.id} entry={entry} scopedTo={entityType} />
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryEntry({
  entry,
  scopedTo,
}: {
  entry: EditHistoryEntry;
  /** The entity_type the drawer is opened for. When it differs from
   *  `entry.entity_type` (e.g. release drawer showing a release_item edit),
   *  we render a small chip so the origin is obvious. */
  scopedTo: string;
}) {
  const actor = entry.actor_email?.split("@")[0] ?? "someone";
  const verb = ACTION_LABEL[entry.action] ?? entry.action;
  const isChild = entry.entity_type !== scopedTo;
  const childLabel = isChild ? (ENTITY_LABEL[entry.entity_type] ?? entry.entity_type) : null;
  const revert = useRevertHistory();
  const confirm = useConfirm();
  const track = useTrack();
  const [revertErr, setRevertErr] = useState<string>("");
  // Only 'update' entries are revertible (publish/unpublish have their own
  // action buttons on the entity page; delete/create aren't undoable via
  // this path). Reverts with zero changes are also pointless.
  const revertible = entry.action === "update" && entry.changes.length > 0;

  const doRevert = async () => {
    setRevertErr("");
    const fieldList = entry.changes
      .map((c) => FIELD_LABEL[c.field] ?? c.field)
      .join(", ");
    const ok = await confirm({
      title: "Revert this change?",
      description: (
        <>
          Restores <strong>{fieldList}</strong> to the values from before this
          edit. A new history entry will be created marking the revert.
        </>
      ),
      confirmLabel: "Revert",
    });
    if (!ok) return;
    try {
      await revert.mutateAsync(entry.id);
      track({
        event: "revert",
        entity_type: entry.entity_type,
        entity_id: entry.entity_id,
        meta: { history_id: entry.id, fields: entry.changes.map((c) => c.field) },
      });
    } catch (e) {
      setRevertErr(
        e instanceof ApiError
          ? e.detail
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  };

  return (
    <section className="rounded-md border border-border bg-card/40 p-3">
      <header className="mb-2 flex flex-wrap items-baseline gap-2 text-sm">
        <span className="font-semibold text-text">{actor}</span>
        <span className="text-text-secondary">{verb}</span>
        {childLabel && (
          <span
            className="rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
            title={`${childLabel} · ${entry.entity_id}`}
          >
            {childLabel}
          </span>
        )}
        <span className="text-text-tertiary">·</span>
        <time className="text-text-secondary">{fmtDateTime(entry.created_at)}</time>
        <span className="ml-auto flex items-center gap-2">
          {revertErr && (
            <span className="text-[11px] text-destructive">{revertErr}</span>
          )}
          {revertible && (
            <button
              type="button"
              onClick={doRevert}
              disabled={revert.isPending}
              className="text-[11px] text-text-tertiary underline decoration-dotted hover:text-text disabled:opacity-50"
              title="Restore the pre-edit values"
            >
              {revert.isPending ? "Reverting…" : "Revert"}
            </button>
          )}
        </span>
      </header>
      {entry.changes.length === 0 ? (
        <p className="text-xs text-text-tertiary">(no field-level changes)</p>
      ) : (
        <ul className="space-y-2">
          {entry.changes.map((c, i) => (
            <li key={i} className="text-xs">
              <ChangeRow change={c} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ChangeRow({ change }: { change: EditHistoryChange }) {
  const label = FIELD_LABEL[change.field] ?? change.field;
  return (
    <div>
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
        {label}
      </div>
      <div className="grid gap-1 md:grid-cols-2">
        <ValueBlock kind="before" value={change.before} />
        <ValueBlock kind="after" value={change.after} />
      </div>
    </div>
  );
}

function ValueBlock({ kind, value }: { kind: "before" | "after"; value: unknown }) {
  const rendered = useMemo(() => renderValue(value), [value]);
  const isEmpty = rendered.trim().length === 0;
  return (
    <div
      className={
        kind === "before"
          ? "rounded border border-border bg-bg/60 px-2 py-1.5 text-[11.5px] text-text-secondary"
          : "rounded border border-border-hover bg-card px-2 py-1.5 text-[11.5px] text-text"
      }
    >
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
        {kind === "before" ? "Before" : "After"}
      </div>
      {isEmpty ? (
        <span className="italic text-text-tertiary">(empty)</span>
      ) : (
        <pre className="whitespace-pre-wrap break-words font-sans">{rendered}</pre>
      )}
    </div>
  );
}

function renderValue(v: unknown): string {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map((x) => String(x)).join(", ");
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}
